import { dcrRegistrationChanged, withoutDiscovery } from "../connectors/dcr";
import { disconnectOAuthConnection } from "./resetOAuthConnection";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { canAuthorizeVisitors } from "../connectors/isDraft";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";
import { stringRecord } from "./utils";

export const updateOAuthConnectionSchema = z.object({
  oauthConnectionId: z.string(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  scopes: z.array(z.string()).optional(),
  status: z.literal("obtained_code").optional(),
  code: z.string().optional(),
  codeVerifier: z.string().optional(),
  redirectUri: z.string().optional(),
  configuration: stringRecord.optional(),
  username: z.string().optional(),
  password: z.string().optional(),
});

export const updateOAuthConnection = mutator({
  redactKeys: [
    "clientId",
    "clientSecret",
    "codeVerifier",
    "code",
    "redirectUri",
    "username",
    "password",
  ],
  schema: updateOAuthConnectionSchema,
  resolve: {
    connection: { kind: "oauthConnection", from: "oauthConnectionId" },
  },
  permissions: [
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.connection.connectedApps?.[0]?.id ?? "",
    },
  ],
  fn: async ({
    tx,
    args: { connection, clientId, scopes, status, configuration },
  }) => {
    await tx.mutate.oauthConnections.update({
      id: connection.id,
      ...(status !== undefined && { status }),
      updatedAt: Date.now(),
    });

    const [connector] = await tx.run(
      zql.connectedApps.where("oauthConnectionId", connection.id)
    );
    if (connector) {
      const appConfiguration = (connector.configuration ?? {}) as Record<
        string,
        string
      >;
      const changes = {
        ...(configuration && {
          configuration: { ...appConfiguration, ...configuration },
        }),
        ...(clientId !== undefined && { oauthClientId: clientId }),
        ...(scopes !== undefined && { oauthRequestedScopes: scopes }),
      };
      if (
        connector.authorizationMode === "visitor" &&
        !canAuthorizeVisitors({
          ...connector,
          oauthConnection: connection,
          ...changes,
        })
      ) {
        throw userFacingError(
          "Switch to owner authorization before changing how this connector authenticates"
        );
      }
      const resetClient =
        clientId === undefined &&
        dcrRegistrationChanged(connector, { ...connector, ...changes });
      await tx.mutate.connectedApps.update({
        id: connector.id,
        ...changes,
        ...(resetClient && {
          oauthClientId: "",
          hasOAuthClientSecret: false,
          configuration: withoutDiscovery(
            changes.configuration ?? appConfiguration
          ),
        }),
      });
      if (resetClient) await disconnectOAuthConnection(tx, connection.id);
    }
  },
});
